import React, { useMemo, useCallback, useState, useRef } from 'react';
import {
  Button,
  Dropdown,
  IconButton,
  Flex,
  Text,
  Toggle,
} from '@vibe/core';
import {
  Settings,
  NavigationChevronLeft,
  NavigationChevronRight,
  Retry,
  Download,
} from '@vibe/icons';
import { useScheduleStore, type ViewMode } from '../../stores/scheduleStore';
import { useWorkloadStore, type WorkloadIndicatorType } from '../../stores/workloadStore';
import { useDateNavigation } from '../../hooks/useDateNavigation';
import WorkloadModeSelectorDialog from './WorkloadModeSelectorDialog';

// ─── Props ────────────────────────────────────────────────────────────────────

interface TopBarProps {
  onOpenSettings: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onExportExcel: () => void;
  isExporting?: boolean;
}

// ─── View mode options ────────────────────────────────────────────────────────

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'week-daily', label: 'Week - Daily' },
  { value: '2weeks-daily', label: '2 Weeks - Daily' },
  { value: 'month-daily', label: 'Month - Daily' },
  { value: 'year-monthly', label: 'Year - Monthly' },
  { value: 'year-daily', label: 'Year - Daily' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const TopBar: React.FC<TopBarProps> = ({
  onOpenSettings,
  onRefresh,
  isRefreshing,
  onExportExcel,
  isExporting,
}) => {
  const { viewMode, setViewMode, hideWeekends, setHideWeekends } = useScheduleStore();
  const {
    indicatorType,
    enabled: workloadEnabled,
    toggleEnabled,
  } = useWorkloadStore();
  const { goToToday, goForward, goBackward, dateRangeLabel } =
    useDateNavigation();

  const [modeSelectorOpen, setModeSelectorOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedViewOption = useMemo(
    () => VIEW_MODE_OPTIONS.find((o) => o.value === viewMode) ?? VIEW_MODE_OPTIONS[0],
    [viewMode],
  );

  // Label for the trigger chip
  const indicatorLabel = useMemo(() => {
    const labels: Record<string, string> = {
      worklog_workload: 'Worklog + Workload',
      remaining_estimate: 'Remaining Estimate',
      availability: 'Availability',
      workload: 'Original Estimate',
      issues: 'Number of Items',
    };
    return indicatorType ? labels[indicatorType] ?? 'Workload' : 'Workload';
  }, [indicatorType]);

  const handleTriggerClick = useCallback(() => {
    if (!workloadEnabled) {
      toggleEnabled();
    }
    setModeSelectorOpen((prev) => !prev);
  }, [workloadEnabled, toggleEnabled]);

  return (
    <div className="rp-topbar">
      {/* Left section: View mode + date navigation */}
      <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} className="rp-topbar__left">
        {/* View mode dropdown */}
        <Dropdown
          size={Dropdown.sizes.SMALL}
          options={VIEW_MODE_OPTIONS}
          value={selectedViewOption}
          clearable={false}
          onChange={(option) => {
            if (option) {
              const selected = option as { value: ViewMode; label: string };
              setViewMode(selected.value);
            }
          }}
          className="rp-topbar__view-dropdown"
        />

        {/* Today button */}
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          onClick={goToToday}
        >
          Today
        </Button>

        {/* Date navigation arrows */}
        <IconButton
          icon={NavigationChevronLeft}
          size={IconButton.sizes.SMALL}
          onClick={goBackward}
          ariaLabel="Previous period"
          className="rp-topbar__nav-arrow"
        />
        <IconButton
          icon={NavigationChevronRight}
          size={IconButton.sizes.SMALL}
          onClick={goForward}
          ariaLabel="Next period"
          className="rp-topbar__nav-arrow"
        />

        {/* Date range label */}
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM} style={{ fontSize: 14 }}>
          {dateRangeLabel}
        </Text>

        {/* Refresh */}
        <IconButton
          icon={Retry}
          size={IconButton.sizes.SMALL}
          onClick={onRefresh}
          ariaLabel="Refresh schedule"
          disabled={isRefreshing}
          className={isRefreshing ? 'rp-topbar__refresh--spinning' : undefined}
        />
      </Flex>

      {/* Right section: Actions */}
      <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} className="rp-topbar__right">
        {/* Workload indicator controls */}
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS} className="rp-topbar__workload">
          <Toggle
            isSelected={workloadEnabled}
            onChange={toggleEnabled}
            size="small"
            ariaLabel="Toggle workload indicators"
          />
          {workloadEnabled ? (
            <button
              ref={triggerRef}
              className="rp-mode-trigger"
              onClick={handleTriggerClick}
            >
              {indicatorLabel}
              <span className={`rp-mode-trigger__chevron${modeSelectorOpen ? ' rp-mode-trigger__chevron--open' : ''}`}>
                &#x25BC;
              </span>
            </button>
          ) : (
            <Text
              type={Text.types.TEXT2}
              className="rp-topbar__workload-label"
              onClick={handleTriggerClick}
              style={{ cursor: 'pointer' }}
            >
              Workload
            </Text>
          )}

          {modeSelectorOpen && triggerRef.current && (
            <WorkloadModeSelectorDialog
              anchorRect={triggerRef.current.getBoundingClientRect()}
              onClose={() => setModeSelectorOpen(false)}
            />
          )}
        </Flex>

        <div className="rp-topbar__divider" />

        {/* Hide weekends toggle */}
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.XS}>
          <Toggle
            isSelected={hideWeekends}
            onChange={() => setHideWeekends(!hideWeekends)}
            size="small"
            ariaLabel="Hide weekends"
          />
          <Text type={Text.types.TEXT2} className="rp-topbar__workload-label" style={{ fontSize: 12 }}>
            Hide weekends
          </Text>
        </Flex>

        <div className="rp-topbar__divider" />

        {/* Export Excel */}
        <IconButton
          icon={Download}
          size={IconButton.sizes.SMALL}
          onClick={onExportExcel}
          ariaLabel="Export schedule to Excel"
          disabled={isExporting}
          className={isExporting ? 'rp-topbar__refresh--spinning' : undefined}
        />

        {/* Settings */}
        <IconButton
          icon={Settings}
          size={IconButton.sizes.SMALL}
          onClick={onOpenSettings}
          ariaLabel="Board settings"
        />
      </Flex>
    </div>
  );
};

export default TopBar;
